// Tile maps for the top-down and platformer games: a grid of tile ids, solid lookup,
// AABB collision with sliding, and a camera that follows a target inside the map bounds.

export interface TileMap {
  cols: number;
  rows: number;
  tile: number;              // CSS px per tile at scale 1
  data: Uint8Array;          // tile ids, row-major
  solid: (id: number) => boolean;
}

export function mapFromStrings(rows: string[], legend: Record<string, number>, tile: number, solidIds: number[]): TileMap {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const data = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const ch = rows[y][x] ?? ' ';
    data[y * w + x] = legend[ch] ?? 0;
  }
  const solidSet = new Set(solidIds);
  return { cols: w, rows: h, tile, data, solid: id => solidSet.has(id) };
}

export function tileAt(m: TileMap, tx: number, ty: number): number {
  if (tx < 0 || ty < 0 || tx >= m.cols || ty >= m.rows) return 255; // outside is solid
  return m.data[ty * m.cols + tx];
}

export function setTile(m: TileMap, tx: number, ty: number, id: number) {
  if (tx < 0 || ty < 0 || tx >= m.cols || ty >= m.rows) return;
  m.data[ty * m.cols + tx] = id;
}

export function solidAt(m: TileMap, px: number, py: number): boolean {
  const id = tileAt(m, Math.floor(px / m.tile), Math.floor(py / m.tile));
  return id === 255 || m.solid(id);
}

export interface Body { x: number; y: number; w: number; h: number; vx: number; vy: number; onGround?: boolean }

/** Move a body by its velocity with axis-separated collision against solid tiles. */
export function moveBody(m: TileMap, b: Body, dt: number): { hitX: boolean; hitY: boolean } {
  let hitX = false, hitY = false;
  // x axis
  b.x += b.vx * dt;
  if (b.vx > 0) {
    if (solidAt(m, b.x + b.w, b.y) || solidAt(m, b.x + b.w, b.y + b.h - 0.01) || solidAt(m, b.x + b.w, b.y + b.h / 2)) {
      b.x = Math.floor((b.x + b.w) / m.tile) * m.tile - b.w - 0.01; b.vx = 0; hitX = true;
    }
  } else if (b.vx < 0) {
    if (solidAt(m, b.x, b.y) || solidAt(m, b.x, b.y + b.h - 0.01) || solidAt(m, b.x, b.y + b.h / 2)) {
      b.x = (Math.floor(b.x / m.tile) + 1) * m.tile + 0.01; b.vx = 0; hitX = true;
    }
  }
  // y axis
  b.y += b.vy * dt;
  if (b.vy > 0) {
    if (solidAt(m, b.x, b.y + b.h) || solidAt(m, b.x + b.w - 0.01, b.y + b.h) || solidAt(m, b.x + b.w / 2, b.y + b.h)) {
      b.y = Math.floor((b.y + b.h) / m.tile) * m.tile - b.h - 0.01; b.vy = 0; hitY = true;
    }
  } else if (b.vy < 0) {
    if (solidAt(m, b.x, b.y) || solidAt(m, b.x + b.w - 0.01, b.y) || solidAt(m, b.x + b.w / 2, b.y)) {
      b.y = (Math.floor(b.y / m.tile) + 1) * m.tile + 0.01; b.vy = 0; hitY = true;
    }
  }
  // standing on something? (checked every step so a resting body stays grounded)
  const under = b.y + b.h + 0.6;
  b.onGround = solidAt(m, b.x + 0.5, under) || solidAt(m, b.x + b.w - 0.5, under) || solidAt(m, b.x + b.w / 2, under);
  return { hitX, hitY };
}

export function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export interface Camera { x: number; y: number; w: number; h: number }

/** Center the camera on a point, clamped to the map. */
export function followCamera(cam: Camera, m: TileMap, cx: number, cy: number, lerp = 0.15) {
  const maxX = Math.max(0, m.cols * m.tile - cam.w), maxY = Math.max(0, m.rows * m.tile - cam.h);
  const tx = Math.max(0, Math.min(maxX, cx - cam.w / 2));
  const ty = Math.max(0, Math.min(maxY, cy - cam.h / 2));
  cam.x += (tx - cam.x) * lerp;
  cam.y += (ty - cam.y) * lerp;
  if (Math.abs(tx - cam.x) < 0.3) cam.x = tx;
  if (Math.abs(ty - cam.y) < 0.3) cam.y = ty;
}

/** Draw only the visible tiles. drawTile gets the tile id and its screen rect. */
export function drawMap(ctx: CanvasRenderingContext2D, m: TileMap, cam: Camera, drawTile: (id: number, sx: number, sy: number, size: number, tx: number, ty: number) => void) {
  const t = m.tile;
  const x0 = Math.max(0, Math.floor(cam.x / t)), y0 = Math.max(0, Math.floor(cam.y / t));
  const x1 = Math.min(m.cols - 1, Math.ceil((cam.x + cam.w) / t)), y1 = Math.min(m.rows - 1, Math.ceil((cam.y + cam.h) / t));
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
    drawTile(m.data[ty * m.cols + tx], Math.round(tx * t - cam.x), Math.round(ty * t - cam.y), t, tx, ty);
  }
}

/** Simple on-screen directional pad + action button for the genre games. Returns held state. */
export interface Pad { left: boolean; right: boolean; up: boolean; down: boolean; action: boolean; destroy(): void; el: HTMLElement }

export function makePad(host: HTMLElement, opts: { action?: string; vertical?: boolean } = {}): Pad {
  const pad: Pad = { left: false, right: false, up: false, down: false, action: false, destroy, el: document.createElement('div') };
  const el = pad.el;
  el.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:12px;padding:8px 4px 4px;user-select:none;';
  const mk = (label: string, key: keyof Pad, wide = false) => {
    const b = document.createElement('button');
    b.textContent = label;
    b.className = 'btn';
    b.style.cssText = `min-width:${wide ? 96 : 56}px;min-height:56px;font-size:22px;touch-action:none;`;
    const on = (e: PointerEvent) => { e.preventDefault(); (pad as any)[key] = true; b.style.background = 'var(--surface-2)'; };
    const off = () => { (pad as any)[key] = false; b.style.background = ''; };
    b.addEventListener('pointerdown', on); b.addEventListener('pointerup', off); b.addEventListener('pointercancel', off); b.addEventListener('pointerleave', off);
    return b;
  };
  const left = document.createElement('div'); left.style.cssText = 'display:grid;grid-template-columns:repeat(3,56px);grid-template-rows:repeat(' + (opts.vertical ? 3 : 1) + ',56px);gap:6px;align-items:center;justify-items:center;';
  if (opts.vertical) {
    left.append(document.createElement('span'), mk('▲', 'up'), document.createElement('span'));
    left.append(mk('◀', 'left'), document.createElement('span'), mk('▶', 'right'));
    left.append(document.createElement('span'), mk('▼', 'down'), document.createElement('span'));
  } else {
    left.append(mk('◀', 'left'), document.createElement('span'), mk('▶', 'right'));
  }
  el.append(left);
  el.append(mk(opts.action ?? 'Jump', 'action', true));
  host.append(el);
  // keyboard for desktop testing
  const kd = (e: KeyboardEvent) => { if (e.key === 'ArrowLeft') pad.left = true; if (e.key === 'ArrowRight') pad.right = true; if (e.key === 'ArrowUp') pad.up = true; if (e.key === 'ArrowDown') pad.down = true; if (e.key === ' ' || e.key === 'z') { pad.action = true; e.preventDefault(); } };
  const ku = (e: KeyboardEvent) => { if (e.key === 'ArrowLeft') pad.left = false; if (e.key === 'ArrowRight') pad.right = false; if (e.key === 'ArrowUp') pad.up = false; if (e.key === 'ArrowDown') pad.down = false; if (e.key === ' ' || e.key === 'z') pad.action = false; };
  window.addEventListener('keydown', kd); window.addEventListener('keyup', ku);
  function destroy() { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); el.remove(); }
  return pad;
}
